import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { ObjectiveFormModal } from '../features/objectives/ObjectiveFormModal';
import { createObjective, fetchObjectives } from '../features/objectives/objectivesApi';
import type { ObjectiveStatus, QualityObjective } from '../features/objectives/types';
import {
  OBJECTIVE_STATUS_LABEL,
  progressBarClass,
} from '../features/objectives/objectiveUtils';
import { DateCell } from '../components/DateCell';

const STATUSES: ObjectiveStatus[] = ['borrador', 'activo', 'logrado', 'no_logrado', 'cancelado'];

export function ObjectivesPage() {
  const { canEdit } = usePlatformAccessLevel('objectives.access');
  const [items, setItems] = useState<QualityObjective[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchObjectives({
        page,
        pageSize: 20,
        search: search || undefined,
        status: filterStatus || undefined,
      });
      setItems(result.data);
      setTotalPages(result.pagination.pages);
    } catch {
      setError('No se pudieron cargar los objetivos.');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Objetivos de calidad</h1>
          <p className="text-muted-foreground text-sm">
            Metas, tareas planificadas e indicadores (ISO 6.2 / 9.1)
          </p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
            Nuevo objetivo
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          className="rounded-md border border-border px-3 py-2 text-sm min-w-[200px]"
          placeholder="Buscar…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="rounded-md border border-border px-3 py-2 text-sm"
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {OBJECTIVE_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-muted/50 text-muted-foreground border-b border-border">
            <tr>
              <th className="px-4 py-3">Código</th>
              <th className="px-4 py-3">Título</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Período</th>
              <th className="px-4 py-3">Progreso tareas</th>
              <th className="px-4 py-3">Indicadores</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                  No hay objetivos registrados.
                </td>
              </tr>
            ) : (
              items.map((obj) => {
                const progress = obj.progress ?? { done: 0, total: 0, percent: 0 };
                return (
                  <tr key={obj.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 font-mono text-xs">{obj.code}</td>
                    <td className="px-4 py-3">
                      <Link to={`/objetivos/${obj.id}`} className="font-medium text-primary hover:underline">
                        {obj.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{OBJECTIVE_STATUS_LABEL[obj.status]}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <DateCell value={obj.periodStart} /> — <DateCell value={obj.periodEnd} />
                    </td>
                    <td className="px-4 py-3 min-w-[140px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full ${progressBarClass(progress.percent)}`}
                            style={{ width: `${progress.percent}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground w-10 text-right">
                          {progress.percent}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">{obj._count?.indicators ?? 0}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm text-muted-foreground self-center">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      <ObjectiveFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSubmit={async (payload) => {
          await createObjective(payload);
          await load();
        }}
      />
    </div>
  );
}
